import React, { useState } from 'react';
import styled from '@emotion/styled';
import { css, SerializedStyles } from '@emotion/core';
import { gridSize } from '@atlaskit/theme';
import {
  N500,
  N400,
  N300,
  N40,
  N30,
  N20,
  N0,
  B400,
} from '@atlaskit/theme/colors';
import { CodeBlock } from '@atlaskit/code';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/glyph/chevron-up';

import ErrorBoundary from './error-boundary';
import replaceSrc from '../utilities/replaceSrc';

const TRANSITION_DURATION = '200ms';
const EXAMPLE_HINT_HEIGHT = 150;
const LINE_TO_HEIGHT_FACTOR = 1.66667 * 12;
const GRADIENT_PERCENTAGE = 75;

type Background = 'white' | 'blue' | 'neutral' | 'checkered';

interface ExampleProps {
  /** The component to render in the example  */
  Component: any;

  /** The source code for the component; if none is provided, will be generated
   * from the `Component` rendered.
   */
  source?: string;

  /** The language in which the code is written */
  language?: string;

  /** The name of the package demonstrated in the example. Used to replace relative references
   * in the example code
   */
  packageName?: string;

  /** Comma and hyphen-separated list of lines to highlight in the source view  */
  highlight?: string;

  /** Controls the visibility of the source section  */
  sourceVisible?: boolean;

  /** Height of the collapsed source view, in pixels */
  hintHeight?: number;

  /** Controls the background color of the example showcase */
  backgroundColor?: Background;
}

export default (props: ExampleProps) => {
  const {
    Component,
    source,
    language = 'javascript',
    packageName,
    highlight = '',
    sourceVisible = true,
    hintHeight = EXAMPLE_HINT_HEIGHT,
    backgroundColor = 'checkered',
  } = props;

  const [collapsed, setCollapsed] = useState(true);
  const componentSource = source || Component.__raw;
  const transformedSource = packageName
    ? replaceSrc(componentSource, packageName)
    : componentSource;
  const showCollapseUI =
    transformedSource.split(/\r\n|\r|\n/).length >
    hintHeight / LINE_TO_HEIGHT_FACTOR;

  return (
    <ExampleWrapper>
      <ExampleShowcase backgroundColor={backgroundColor}>
        <ErrorBoundary
          fallback={<h4>Something went wrong loading this example.</h4>}
        >
          <Component />
        </ErrorBoundary>
      </ExampleShowcase>
      {sourceVisible && (
        <>
          <ExampleCode
            collapsed={showCollapseUI ? collapsed : false}
            hintHeight={hintHeight}
            onMouseDown={() => setCollapsed(false)}
          >
            <CodeBlock
              text={transformedSource}
              language={language}
              showLineNumbers={false}
              highlight={highlight}
            />
          </ExampleCode>
          {showCollapseUI && (
            <CollapseButton
              collapsed={collapsed}
              onClick={() => {
                setCollapsed(!collapsed);
              }}
            >
              {collapsed ? (
                <>
                  <span>Show more</span>
                  <ChevronDownIcon label="show more" />
                </>
              ) : (
                <>
                  <span>Show less</span>
                  <ChevronUpIcon label="show less" />
                </>
              )}
            </CollapseButton>
          )}
        </>
      )}
    </ExampleWrapper>
  );
};

const ExampleWrapper = styled.div`
  border-radius: 4px 4px 4px 4px;
  box-shadow: 0 0 0 1.2px #ebecf0;
`;

type ExampleShowcaseProps = {
  backgroundColor?: Background;
};

const getBackgroundColor = (code: Background): SerializedStyles => {
  switch (code) {
    case 'white':
      return css`
        background-color: ${N0};
      `;

    case 'blue':
      return css`
        background-color: ${B400};
      `;

    case 'neutral':
      return css`
        background-color: ${N40};
      `;

    default:
    case 'checkered':
      return css`
        background-color: white;
        background-image: linear-gradient(45deg, #f9f9fa 25%, transparent 25%),
          linear-gradient(135deg, #f9f9fa 25%, transparent 25%),
          linear-gradient(45deg, transparent 75%, #f9f9fa 75%),
          linear-gradient(135deg, transparent 75%, #f9f9fa 75%);
      `;
  }
};

const ExampleShowcase = styled.div<ExampleShowcaseProps>`
  padding: ${gridSize() * 3}px;
  ${({ backgroundColor }) => getBackgroundColor(backgroundColor)}
  background-size: 20px 20px;
  background-position: 0 0, 10px 0, 10px -10px, 0px 10px;
  border-radius: 3px 3px 0 0;
`;

type ExampleCodeProps = {
  collapsed: boolean;
  hintHeight?: number;
};

const ExampleCode = styled.div<ExampleCodeProps>`
  padding-bottom: 2px;
  background-color: ${N20};

  code {
    padding: ${gridSize() * 2}px ${gridSize() * 3}px !important;
    max-height: ${p => (p.collapsed ? `${p.hintHeight}px` : 'none')};
    white-space: pre-wrap;
    overflow: hidden;
  }

  & > span {
    --mask: linear-gradient(
      to bottom,
      black,
      black ${100 - GRADIENT_PERCENTAGE}%,
      transparent 90%
    );
    mask: ${p => (p.collapsed ? 'var(--mask)' : 'none')};
    position: relative;
  }
`;

type CollapseButtonProps = {
  collapsed: boolean;
};

const CollapseButton = styled.button<CollapseButtonProps>`
  /* Layout */
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: ${gridSize() * 4}px;

  /* Colour and borders */
  background-color: ${N20};
  border-radius: 0 0 4px 4px;
  border-width: 0px;
  transition: background ${TRANSITION_DURATION} box-shadow 0.15s
    cubic-bezier(0.47, 0.03, 0.49, 1.38);
  outline: none;

  /* Button properties */
  cursor: pointer;

  /* Text */
  font-family: 'Charlie Display', -apple-system, BlinkMacSystemFont, 'Segoe UI',
    'Roboto', 'Oxygen', 'Ubuntu', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
    sans-serif;
  font-weight: 500;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${N300};

  :hover {
    background: ${N30};
    color: ${N400};
  }

  :active {
    background: ${N40};
    color: ${N500};
  }

  :focus {
    box-shadow: 0 0 0 2px rgba(38, 132, 255, 0.6);
  }
`;
